// src/Pages/ProductPage/ProductPage.jsx
import { useBooking } from "../../context/BookingContext"; // 🔥 Haetaan päivämäärät BookingContextista
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { MdArrowBack, MdAddShoppingCart, MdRemoveShoppingCart, MdCalendarToday } from "react-icons/md";
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
  EmailIcon,
} from "react-share";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ResponsiveButton from "../../components/ResponsiveButton/ResponsiveButton"; // polku tarpeen mukaan

// 🔥 Tuodaan TrailerCalendar (nimi pysyy WeeklyTrailerCalendar, jos haluat)
// mutta oletus nimen mukaan:
import TrailerCalendar from "../../components/TrailerCalendar/TrailerCalendar";

/**
 * Palauttaa varaston määrän numerona.
 * - Ensin tarkistaa 'kappalemaara'
 * - Jos sitä ei ole käytettävissä, parsii 'available'.
 */
function getStockValue(item) {
  const fromKappale = parseInt(item.kappalemaara, 10);
  if (!isNaN(fromKappale) && fromKappale > 0) {
    return fromKappale;
  }
  if (typeof item.available === "string" && item.available.includes("/")) {
    const parts = item.available.split("/");
    return parseInt(parts[1], 10) || 0;
  }
  return parseInt(item.available, 10) || 0;
}

// Haetaan ostoskori localStoragesta
function getCart() {
  return JSON.parse(localStorage.getItem("rentalCart")) || [];
}
function getProductQuantityInCart(slug) {
  return getCart()
    .filter((item) => item.slug === slug)
    .reduce((sum, item) => sum + item.quantity, 0);
}

// Tarkistaa, menevätkö kaksi aikaväliä päällekkäin
function isOverlap(start1, end1, start2, end2) {
  return start1 <= end2 && start2 <= end1;
}

const ProductPage = () => {
  const { selectedDates, setSelectedDates } = useBooking();
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

    // 🔥 Avuksi lista peräkärry-kriteereistä
    const trailerKeywords = [
      "peräkärry",
      "perakarry",
      "traileri",
      "hirvi 350",
      "kuomukärry",
      "kärry",
      "karry",
    ];

  // Kamerapakettien state
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState("");

  // Tuotteen tiedot
  const [product, setProduct] = useState(null);

    // Onko tuote peräkärry?
    const isTrailer =
    product &&
    trailerKeywords.some(
      (keyword) =>
        product.name?.toLowerCase().includes(keyword) ||
        product.slug?.toLowerCase().includes(keyword)
    );
  

  const [activeImg, setActiveImg] = useState(null);

  // Saatavuus & varaukset
  const [available, setAvailable] = useState(0);
  const [reservations, setReservations] = useState([]);

  // Onko tuote korissa?
  const [isAddedToCart, setIsAddedToCart] = useState(false);

  // Onko tuote täysin varattu
  const [isBooked, setIsBooked] = useState(false);

  // Montako on varattu
  const [bookedCount, setBookedCount] = useState(0);

  // Määrän valinta – näytetään vain, jos kyseessä ei ole kameratuote
  const [selectedQuantity, setSelectedQuantity] = useState(1);

  // Ladataan mahdollisesti jo valitut päivämäärät localStoragesta
  useEffect(() => {
    const savedDates = JSON.parse(localStorage.getItem("modalBookingData"));
    if (savedDates && savedDates.startDate && savedDates.endDate) {
      setSelectedDates(savedDates);
    }
  }, [setSelectedDates]);

  // Jos sivulle tullaan /tuote/slug#kalenteri -> scrollaa kalenteriin
  useEffect(() => {
    if (location.hash === "#kalenteri") {
      const interval = setInterval(() => {
        const element = document.getElementById("kalenteri");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
          clearInterval(interval);
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, [location, product]);

  // 1) Haetaan varaukset
  useEffect(() => {
    const fetchReservations = async () => {
      try {
        const apiUrl = isTrailer
          ? "https://spiderlegsproductions.com/api/get_trailer_reservations.php"
          : "https://spiderlegsproductions.com/api/get_reservations.php";
        const res = await fetch(apiUrl);
        const data = await res.json();
        if (data.success) {
          setReservations(data.reservations);
        } else {
          console.error("Virhe varauksia haettaessa:", data.error);
        }
      } catch (err) {
        console.error("Virhe varauksia haettaessa:", err);
      }
    };
  
    fetchReservations();
  }, [isTrailer]);
  
// 2) Tarkistetaan, onko tuote täysin varattu
const checkIfProductIsFullyBooked = useCallback(
  (foundItem) => {
    // 🔐 Tarkista, että kaikki tarvittavat arvot on saatavilla ennen jatkamista
    if (!foundItem || !selectedDates?.startDate || !selectedDates?.endDate) return;

    const stock = getStockValue(foundItem) || 1;
    let totalBooked = 0;

    reservations.forEach((res) => {
      if (res.start && res.end) {
        const s = new Date(selectedDates.startDate);
        const e = new Date(selectedDates.endDate);
        const rStart = new Date(res.start);
        const rEnd = new Date(res.end);

        if (isOverlap(s, e, rStart, rEnd)) {
          if (Array.isArray(res.products)) {
            res.products.forEach((p) => {
              if (String(p.tuoteID).trim() === String(foundItem.id).trim()) {
                totalBooked += parseInt(p.quantity, 10) || 0;
              }
            });
          }
        }
      }
    });

    setBookedCount(totalBooked);
    setIsBooked(totalBooked >= stock);
  },
  [reservations, selectedDates]
);

  

  // Pieni apufunktio automaattisen metakuvauksen generointiin
  const generateProductMetaDescription = (product) => {
    if (!product || !product.name)
      return "Tutustu vuokrattaviin tuotteisiin – Spiderlegs Productions tarjoaa laadukkaat laitteet juhliin ja tapahtumiin.";

    const name = product.name;
    const category = product.category?.toLowerCase() || "";
    const price = parseFloat(product.price);
    const formattedPrice = isNaN(price) ? "" : `Alkaen ${price}€/vrk. `;

    let base = "";
    if (category.includes("kaiutin") || name.toLowerCase().includes("kaiutin")) {
      base = `Täydellinen äänentoistoratkaisu juhliin, keikoille ja puheisiin – ${name}.`;
    } else if (
      category.includes("kamera") ||
      name.toLowerCase().includes("instax") ||
      name.toLowerCase().includes("kamera")
    ) {
      base = `${name} tallentaa juhlatunnelman – loistava valinta tapahtumiin ja vuokrattavaksi.`;
    } else if (category.includes("valo") || name.toLowerCase().includes("valo")) {
      base = `${name} luo näyttävän valaistuksen juhliin ja tapahtumiin.`;
    } else {
      base = `Vuokraa ${name} helposti – erinomainen valinta tapahtumiin, juhliin ja keikoille.`;
    }

    const full = `${formattedPrice}${base}`.trim();
    return full.length > 160 ? full.slice(0, 157) + "…" : full;
  };

// 3) Haetaan tuote + saatavuus
useEffect(() => {
  const fetchProductData = async () => {
    try {
      const res = await fetch(
        `https://spiderlegsproductions.com/api/get_product_details.php?slug=${slug}`
      );
      const data = await res.json();

      if (!data.success || !data.product) {
        toast.error("Tuotetta ei löytynyt");
        console.error("⚠️ Virhe: Tuotetta ei löytynyt:", data);
        return;
      }

      const productData = data.product;
      setProduct(productData);

      // ✅ Asetetaan pääkuva
      if (
        Array.isArray(productData.images) &&
        productData.images.length > 0 &&
        typeof productData.images[0].image_url === "string" &&
        productData.images[0].image_url.trim() !== ""
      ) {
        const cleanImageUrl = productData.images[0].image_url.replace(/\\\//g, "/");
        setActiveImg(cleanImageUrl);
      } else {
        setActiveImg("https://spiderlegsproductions.com/assets/fallback.jpg");
      }

      // ✅ Haetaan vuokrattavat
      const res2 = await fetch("https://spiderlegsproductions.com/api/get_vuokrattavat.php");
      const data2 = await res2.json();

      if (data2 && data2.success && (Array.isArray(data2.items) || data2.categories)) {
        const allItems = Array.isArray(data2.items)
          ? data2.items
          : Object.values(data2.categories || {}).flat();

        const foundItem = allItems.find((item) => item.slug === slug);

        if (foundItem) {
          const stockValue = getStockValue(foundItem);
          const currentQuantity = getProductQuantityInCart(slug);
          const finalAvailable = stockValue - currentQuantity;
          setAvailable(Math.max(finalAvailable, 0));
          setIsAddedToCart(currentQuantity > 0);
          checkIfProductIsFullyBooked(foundItem);
        } else {
          console.warn("⚠️ Ei löytynyt items-listasta:", slug);
          setAvailable(0);
        }
      } else {
        console.error("⚠️ Virhe: get_vuokrattavat.php ei palauttanut odotettua dataa.", data2);
        toast.error("Virhe saatavuustietojen haussa");
      }

    } catch (error) {
      console.error("⚠️ Virhe tuotetietojen haussa:", error);
      toast.error("Virhe tuotetietojen haussa");
    }
  };

  fetchProductData();
}, [slug, reservations, checkIfProductIsFullyBooked, selectedDates]);


  // 4) Päivitetään saatavuus, jos ostoskoria muutetaan
  useEffect(() => {
    const handleCartUpdate = async () => {
      try {
        const res = await fetch("https://spiderlegsproductions.com/api/get_vuokrattavat.php");
        const data = await res.json();
        if (data.success) {
          const allItems = data.categories
            ? Object.values(data.categories).flat()
            : data.items || [];
  
          const foundItem = allItems.find((item) => item.slug === slug);
          if (foundItem) {
            const stockValue = getStockValue(foundItem);
            const currentQuantity = getProductQuantityInCart(slug);
            const finalAvailable = stockValue - currentQuantity;
            setAvailable(Math.max(finalAvailable, 0));
            setIsAddedToCart(currentQuantity > 0);
            checkIfProductIsFullyBooked(foundItem);
          } else {
            setAvailable(0);
          }
        }
      } catch (err) {
        console.error("Virhe ostoskorin päivityksessä:", err);
      }
    };
    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => window.removeEventListener("cartUpdated", handleCartUpdate);
  }, [slug, checkIfProductIsFullyBooked]);
  

  // 5) Jos on Instax-kamera, haetaan kamerapaketit
  useEffect(() => {
    if (product?.slug === "fujifilm-instax-mini-12") {
      const cameraID = product.id || 22;
      const fetchCameraWithPackages = async () => {
        try {
          const resp = await fetch(
            `https://spiderlegsproductions.com/api/get_camera_with_packages.php?cameraID=${cameraID}`
          );
          const data = await resp.json();
          if (!data.success) {
            toast.error("Kamerapaketin haku epäonnistui");
            return;
          }
          setPackages(data.camera.packages || []);
        } catch (err) {
          console.error("Virhe haettaessa kamerapaketteja:", err);
        }
      };
      fetchCameraWithPackages();
    }
  }, [product]);

  // Apufunktio vain nimen käsittelyyn
  const generalizedName = (name) => {
    return name;
  };

  // 6a) Lisää tuote koriin
  const addToCart = () => {

    const currentQuantity = getProductQuantityInCart(slug);
    const stock = getStockValue(product) || 1;
    if (currentQuantity >= stock) {
      toast.error(`Olet jo lisännyt maksimimäärän (${stock} kpl).`);
      return;
    }

    const qtyToAdd = selectedQuantity;
    if (currentQuantity + qtyToAdd > stock) {
      toast.error(`Voit lisätä vain ${stock - currentQuantity} kpl.`);
      return;
    }

    const cartItems = getCart();

    // Estetään yhdistelmä, jossa peräkärry + muita tuotteita on samassa korissa
    const trailerInCart = cartItems.some((item) =>
      trailerKeywords.some(
        (keyword) =>
          item.name.toLowerCase().includes(keyword) ||
          (item.slug && item.slug.toLowerCase().includes(keyword))
      )
    );
    const productIsTrailer = isTrailer;

    // Jos korissa on jo peräkärry, eikä lisättävä tuote ole peräkärry
    if (trailerInCart && !productIsTrailer) {
      toast.error(
        "Et voi lisätä muita tuotteita, kun vuokrauskorissa on jo peräkärry. Poista peräkärry ensin."
      );
      return;
    }
    // Jos lisättävä tuote on peräkärry, mutta korissa on muita tuotteita
    if (!trailerInCart && productIsTrailer && cartItems.length > 0) {
      toast.error(
        "Et voi lisätä peräkärryä, kun vuokrauskorissa on muita tuotteita. Poista muut tuotteet ensin."
      );
      return;
    }

    // Rakennetaan uusi tai päivitetään olemassaolevaa
    const updatedCart = [...cartItems];
    const existingIndex = updatedCart.findIndex((i) => i.slug === slug);
    let productName = product.name;
    let productPrice = parseFloat(product.price) || 0;

    // Jos Instax-kamera + valittu paketti
    if (product.slug === "fujifilm-instax-mini-12") {
      if (!selectedPackage) {
        toast.error("Valitse ensin haluamasi vuokrapaketti!");
        return;
      }
    
const chosenPackage = packages.find((p) => p.packageID === Number(selectedPackage));


    
      if (chosenPackage) {
        productName = `${product.name} (${chosenPackage.name})`;
        productPrice = parseFloat(chosenPackage.price) || 0;

      } else {
        toast.error("Valittu paketti ei löytynyt.");
        return;
      }
    }
    

    if (existingIndex > -1) {
      updatedCart[existingIndex].quantity += qtyToAdd;
      updatedCart[existingIndex].totalPrice =
        updatedCart[existingIndex].quantity * productPrice;
    } else {
      updatedCart.push({
        slug: product.slug,
        id: product.id,
        image: product.image,
        name: productName,
        price: productPrice,
        quantity: qtyToAdd,
        totalPrice: productPrice * qtyToAdd,
      });
    }

    localStorage.setItem("rentalCart", JSON.stringify(updatedCart));
    window.dispatchEvent(new Event("cartUpdated"));

    const newQuantity = currentQuantity + qtyToAdd;
    if (stock === 1) {
      toast.success(`${productName} lisätty vuokrauskoriin!`);
    } else if (newQuantity === stock) {
      toast.success(`Kaikki ${generalizedName(productName)} ovat nyt korissa!`);
    } else {
      toast.success(
        `${qtyToAdd} kpl tuotetta ${productName} lisätty koriin! Nyt korissa on yhteensä ${newQuantity} kpl.`
      );
    }

    setAvailable(stock - newQuantity);
    setIsAddedToCart(true);
  };

  // 6b) Poista tuote korista
  const removeFromCart = () => {
    const qtyToRemove = selectedQuantity;
    const cartItems = getCart();
    const existingIndex = cartItems.findIndex((item) => item.slug === slug);

    if (existingIndex === -1) {
      toast.error("Tuotetta ei ole korissa.");
      return;
    }

    const stock = getStockValue(product) || 1;
    const productName = cartItems[existingIndex].name;
    const currentQuantity = cartItems[existingIndex].quantity;

    if (currentQuantity <= qtyToRemove) {
      // Poistetaan kokonaan korista
      cartItems.splice(existingIndex, 1);
      setIsAddedToCart(false);
      setAvailable((prev) => prev + currentQuantity);
      localStorage.setItem("rentalCart", JSON.stringify(cartItems));
      window.dispatchEvent(new Event("cartUpdated"));

      if (stock === 1) {
        toast.info(`${productName} poistettu korista.`);
      } else {
        toast.info(`Kaikki ${generalizedName(productName)} on nyt poistettu korista!`);
      }
    } else {
      // Poistetaan vain osa
      cartItems[existingIndex].quantity -= qtyToRemove;
      const priceNum = parseFloat(cartItems[existingIndex].price) || 0;
      cartItems[existingIndex].totalPrice =
        cartItems[existingIndex].quantity * priceNum;
      localStorage.setItem("rentalCart", JSON.stringify(cartItems));
      window.dispatchEvent(new Event("cartUpdated"));
      const newQuantity = currentQuantity - qtyToRemove;
      toast.info(
        `${qtyToRemove} kpl tuotetta ${productName} poistettu korista. Nyt korissa on yhteensä ${newQuantity} kpl.`
      );
      setAvailable((prev) => prev + qtyToRemove);
    }
  };

  // Määrän valinta
  const incrementQuantity = () => {
    setSelectedQuantity((prev) => (prev < available ? prev + 1 : prev));
  };
  const decrementQuantity = () => {
    setSelectedQuantity((prev) => (prev > 1 ? prev - 1 : 1));
  };

  // Aktiivinen kuva klikkauksesta
  const handleImageClick = (imgUrl) => {
    setActiveImg(imgUrl);
  };

  // Jos tuote ei vielä ladattu, näytetään "Ladataan..."
  if (!product) {
    return <p>Ladataan tuotetietoja...</p>;
  }

  // Lasketaan varastomäärä
  const stock = getStockValue(product) || 1;
  // Montako jo korissa
  const cartQuantity = getProductQuantityInCart(slug);
  // Paljonko jäljellä käytettävissä
  const freeCountWithCart = Math.max(0, stock - (bookedCount + cartQuantity));

  // Luo SEO-kuvaus
  const metaDesc = generateProductMetaDescription(product);

  // Renderöidään sivu
  return (
    <>
      <Helmet>
        {/* Title ja Meta Description */}
        <title>Spiderlegs Productions | {product.name}</title>
        <meta name="description" content={metaDesc} />
        <meta
          name="keywords"
          content={`vuokraus, äänentoisto, valot, savukoneet, mikseri, juhlat, häät, tapahtumat, esiintymislava, kaiuttimet, Spiderlegs Productions, ${product.name}`}
        />

        {/* Open Graph */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`Spiderlegs Productions | ${product.name}`} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={`https://spiderlegsproductions.com/tuote/${product.slug}`} />
        <meta
          property="og:image"
          content={product.images?.[0]?.image_url || "https://spiderlegsproductions.com/assets/fallback.jpg"}
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Spiderlegs Productions | ${product.name}`} />
        <meta name="twitter:description" content={metaDesc} />
        <meta
          name="twitter:image"
          content={product.images?.[0]?.image_url || "https://spiderlegsproductions.com/assets/fallback.jpg"}
        />

        {/* Canonical */}
        <link rel="canonical" href={`https://spiderlegsproductions.com/tuote/${product.slug}`} />
      </Helmet>

      <div className="pt-20">
        {/* Pieni tyhjä tila */}
        <div className="h-4"></div>

        <ResponsiveButton
          onClick={() => {
            if (window.history.length > 2) {
              navigate(-1);
            } else {
              navigate("/");
            }
            setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
          }}
          variant="backbutton"
          className="ml-4"
        >
          <div className="flex items-center gap-2">
            <MdArrowBack className="text-xl" />
            <span>Takaisin</span>
          </div>
        </ResponsiveButton>

        {/* Jos tuote on peräkärry, näytetään kuva, otsikko+kuvaus ja kalenteri vierekkäin */}
        {isTrailer ? (
          <div className="flex flex-col lg:flex-row xl:flex-row 2xl:flex-row gap-10 items-start px-5 mb-10">
            {/* Vasen sarake: kuva + otsikko + kuvaus */}
            <div id="trailerinfo" className="w-full flex flex-col gap-5">
              {/* Tuotekuva */}
              {activeImg && (
                <div className="relative w-full h-[600px] mdd:h-[700px] md:h-[700px] lg:h-[600px] xl:h-[700px] 2xl:h-[700px] flex justify-center items-center">
                  <img
                    src={activeImg}
                    alt={product.name}
                    title={product.name}
                    className="w-full h-full object-contain rounded-xl drop-shadow-[10px_10px_8px_rgba(0,0,0,0.60)]"
                  />
                </div>
              )}

              {/* Tuotteen otsikko ja kuvaus */}
              <div className="dark:bg-violet bg-primary dark:bg-opacity-75 bg-opacity-75 p-5 rounded-3xl">
                <h1 className="font-customFont2 dark:text-shadow-lg text-left text-darkest dark:text-primary text-4xl font-bold leading-tight mb-4">
                  {product.name}
                </h1>
                <div
                  className="font-customFont1 text-left text-base font-light"
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />

                <div className="my-4">
                <a
                  href="/palvelut#vuokrausehdot"
                  className="text-linkcol underline hover:text-accent transition font-customFont1 text-base"
                >
                  📄 Lue vuokrausehdot ja käyttöohjeet
                </a>

                </div>

                <div className="font-customFont1 dark:text-shadow-lg text-left text-darkest dark:text-primary xxxsm:text-4xl text-3xl sm:text-4xl md:text-2xl lg:text-3xl xl:text-4xl 2xl:text-4xl font-bold leading-tight">
                {product.slug === "fujifilm-instax-mini-12" && selectedPackage
                  ? (() => {
                      const selected = packages.find(p => String(p.packageID) === String(selectedPackage));
                      return selected ? `${selected.package_price}€ / vrk` : "0€ / vrk";
                    })()
                  : `${product.price}€ / vrk`}

                  <span className="font-customFont1 dark:text-shadow-lg text-left text-darkest dark:text-primary xxxsm:text-lg text-lg sm:text-lg md:text-lg lg:text-lg xl:text-xl 1xl:text-1xl font-light pl-2">
                    (Omavastuu vahinkotapauksissa 200 €)
                  </span>
                </div>
              </div>
            </div>

            <div
              id="kalenteri"
              className="scroll-mt-10 bg-transparent rounded-xl shadow-md w-full"
            >
              <TrailerCalendar reservations={reservations} />
            </div>

          </div>
        ) : (
          /* Muut tuotteet (ei-peräkärryt) saavat normaalin layoutin */
          <div className="flex flex-col justify-between lg:flex-row xl:flex-row 2xl:flex-row gap-16 lg:items-start p-5">
            {/* Vasen sarake: Kuvat, pikkukuvat, videot */}
            <div className="flex flex-col gap-5">
              {activeImg && (
                <div className="w-full max-w-[800px] mx-auto flex justify-center">
                  <div className="relative w-full max-w-[800px] h-[450px] sm:h-[400px] md:h-[500px] lg:h-[550px] flex justify-center items-center">
                    {isBooked && (
                      <div
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%) rotate(-15deg)",
                          backgroundColor: "red",
                          color: "white",
                          padding: "20px 50px",
                          fontSize: "45px",
                          fontWeight: "bold",
                          borderRadius: "10px",
                          textTransform: "uppercase",
                          zIndex: 100,
                          boxShadow: "10px 10px 30px rgba(0,0,0,0.4)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          lineHeight: "1.2",
                          flexDirection: "column",
                          opacity: 0.85,
                        }}
                      >
                        Nyt VARATTU
                      </div>
                    )}
                    <img
                      src={activeImg}
                      alt={product.name}
                      title={product.name}
                      className="w-full h-full object-contain rounded-xl drop-shadow-[10px_10px_8px_rgba(0,0,0,0.60)]"
                    />
                  </div>
                </div>
              )}

              {/* Pikkukuvat */}
              {Array.isArray(product.images) && product.images.length > 0 && (
                <div className="flex gap-5 flex-wrap justify-center">
                  {product.images.map((img) => (
                    <button
                      key={img.image_id}
                      onClick={() => handleImageClick(img.image_url)}
                      className="w-32 h-32 rounded-md p-0 border-0 bg-transparent cursor-pointer focus:outline-none"
                    >
                      <img
                        src={img.image_url}
                        alt={img.alt_text || product.name}
                        title={img.alt_text || product.name}
                        className="w-full h-full object-cover rounded-md"
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Videot, jos on */}
              {Array.isArray(product.videos) && product.videos.length > 0 && (
                <>
                  <h2 className="font-customFont2 dark:text-shadow-xxl text-center text-darkest dark:text-primary xxxsm:text-4xl text-5xl sm:text-6xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-6xl font-bold leading-tight p-10">
                    Videot
                  </h2>
                  <div className="flex gap-5 flex-wrap justify-center w-full pb-20">
                    {product.videos.map((vid) => (
                      <div
                        key={vid.video_id}
                        className="relative w-full max-w-4xl"
                        style={{ paddingBottom: "56.25%", height: 0 }}
                      >
                        <iframe
                          className="absolute top-0 left-0 w-full h-full"
                          src={vid.video_url}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={vid.title || product.name}
                        ></iframe>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>


            {/* Oikea sarake: Tuotekuvaus ja ostoskori-napit */}
            <div
              id="trailerinfo"
              className="dark:bg-violet bg-primary dark:bg-opacity-75 bg-opacity-75 flex gap-4 flex-col lg:w-2/4 p-5 rounded-3xl"
            >
              <div>
                {/* Otsikko */}
                <div className="flex flex-row items-center">
                  <h1 className="font-customFont2 dark:text-shadow-lg text-left text-darkest dark:text-primary text-5xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl 2xl:text-6xl font-bold leading-[1.2]">
                    {product.name}
                  </h1>
                </div>
                <br />

                {/* Tuotekuvaus (HTML) */}
                <div
                  className="font-customFont1 text-left text-base font-light"
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
                <br />



                {/* Jako-napit */}
                <div className="flex flex-row py-5 gap-2">
                  <FacebookShareButton
                    url={`https://spiderlegsproductions.com/${product.slug || ""}`}
                  >
                    <FacebookIcon size={32} round />
                  </FacebookShareButton>
                  <TwitterShareButton
                    url={`https://spiderlegsproductions.com/${product.slug || ""}`}
                  >
                    <TwitterIcon size={32} round />
                  </TwitterShareButton>
                  <WhatsappShareButton
                    url={`https://spiderlegsproductions.com/${product.slug || ""}`}
                  >
                    <WhatsappIcon size={32} round />
                  </WhatsappShareButton>
                  <LinkedinShareButton
                    url={`https://spiderlegsproductions.com/${product.slug || ""}`}
                  >
                    <LinkedinIcon size={32} round />
                  </LinkedinShareButton>
                  <EmailShareButton
                    url={`https://spiderlegsproductions.com/${product.slug || ""}`}
                  >
                    <EmailIcon size={32} round />
                  </EmailShareButton>
                </div>

                {/* Hinta + omavastuu (vain jos trailer) */}
                <div className="flex flex-row items-center">
                  <h2
                    title="Hinta"
                    className="font-customFont1 dark:text-shadow-lg text-left text-darkest dark:text-primary xxxsm:text-4xl text-3xl sm:text-4xl md:text-2xl lg:text-3xl xl:text-4xl 2xl:text-4xl font-bold leading-tight"
                  >
                    {product.price}€ / vrk
                  </h2>
                  {isTrailer && (
                    <span className="font-customFont1 dark:text-shadow-lg text-left text-darkest dark:text-primary xxxsm:text-lg text-lg sm:text-lg md:text-lg lg:text-lg xl:text-xl 1xl:text-1xl font-light pl-2">
                      (Omavastuu vahinkotapauksissa 200 €)
                    </span>
                  )}
                </div>

                {/* Saatavilla */}
                <div className="font-customFont1 dark:text-shadow-lg text-left text-darkest dark:text-primary xxxsm:text-lg text-sm font-light mt-4">
                  {(() => {
                    const stock = getStockValue(product) || 1;
                    if (stock > 1) {
                      return (
                        <p>
                          Saatavilla: {freeCountWithCart} / {stock}
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>

                {/* Määrän valinta (jos varastoa > 1, ei trailer eikä instax-kamera) */}
                {!isTrailer &&
                  product.slug !== "fujifilm-instax-mini-12" &&
                  stock > 1 &&
                  !isBooked && (
                    <div className="flex items-center gap-3 mt-5 mb-5">
                      <button
                        onClick={decrementQuantity}
                        className="h-8 w-8 rounded-full bg-darkest text-primary font-bold flex items-center justify-center"
                      >
                        -
                      </button>
                      <span className="font-customFont1 dark:text-shadow-lg text-center text-darkest dark:text-primary text-base font-light">
                        {selectedQuantity}
                      </span>
                      <button
                        onClick={incrementQuantity}
                        className="h-8 w-8 rounded-full bg-darkest text-primary font-bold flex items-center justify-center"
                      >
                        +
                      </button>
                    </div>
                  )}

                {/* Instax-paketit (jos on Instax-kamera) */}
                {product.slug === "fujifilm-instax-mini-12" && (
                  <div className="mt-5">
                    <h2 className="font-customFont2 dark:text-shadow-lg text-left text-darkest dark:text-primary xxxsm:text-4xl text-3xl sm:text-4xl md:text-2xl lg:text-3xl xl:text-4xl 2xl:text-4xl font-bold leading-tight">
                      Valitse vuokrapaketti:
                    </h2>
                    <select
                      className="w-full p-2 border rounded-full mt-2 font-customFont1 text-left text-darkest text-base font-light"
                      value={selectedPackage}
                      onChange={(e) => setSelectedPackage(e.target.value)}
                      required
                    >
                      <option value="" disabled hidden>
                        Valitse paketti
                      </option>
                      {packages.map((pkg) => (
                        <option key={pkg.packageID} value={pkg.packageID}>
                          {pkg.name} – {pkg.price} € ({pkg.film_count} kpl kuvia)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Ostoskori-napit (ei trailer) */}
                {!isTrailer && (
                  <div className="flex flex-wrap justify-start items-center gap-x-4 gap-y-3 pt-5">
                    <ResponsiveButton
                      onClick={addToCart}
                      icon={MdAddShoppingCart}
                      disabled={isBooked || available <= 0}
                      variant="addtocart"
                      className="min-w-[140px] h-10 sm:h-11 lg:h-12 text-xs sm:text-sm md:text-base"
                    >
                      Lisää koriin
                    </ResponsiveButton>

                    {isAddedToCart && !isBooked && (
                      <ResponsiveButton
                        onClick={removeFromCart}
                        icon={MdRemoveShoppingCart}
                        variant="removefromcart"
                        className="min-w-[140px] h-10 sm:h-11 lg:h-12 text-xs sm:text-sm md:text-base"
                      >
                        Poista korista
                      </ResponsiveButton>
                    )}

                    <Link
                      to={isBooked ? "#" : "/tee-varaus"}
                      onClick={(e) => {
                        if (isBooked) e.preventDefault();
                        else window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                      }}
                      className="w-full sm:w-auto"
                    >
                      <ResponsiveButton
                        disabled={isBooked}
                        icon={MdCalendarToday}
                        variant="makeabooking"
                        className="min-w-[140px] h-10 sm:h-11 lg:h-12 text-xs sm:text-sm md:text-base"
                      >
                        Tee varaus
                      </ResponsiveButton>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
    </>
  );
};

export default ProductPage;
